/**
 * Field catalogue of the portfolio usage charts (SPEC-0053 §4.1): every attribute a chart can be
 * grouped or filtered by — standard fields of the usage row and the tenant's custom attributes of
 * projects, scenarios and resources — and the resolver that reads one field off one usage row.
 *
 * Field keys are stable identifiers ("project.priority", "project.custom.42"); they are what the
 * saved chart options and the saved series styles refer to.
 */
import { valueTokens } from "../AnalyticsFilterUtils/analyticsAttributeFilter";

export const ENTITY_PROJECT = "PROJECT";
export const ENTITY_SCENARIO = "SCENARIO";
export const ENTITY_RESOURCE = "RESOURCE";
export const ENTITY_ACTIVITY = "ACTIVITY";
export const ENTITY_DATA = "DATA";
export const ENTITY_ORDER = [ENTITY_PROJECT, ENTITY_SCENARIO, ENTITY_RESOURCE, ENTITY_ACTIVITY, ENTITY_DATA];

/** Id of the "(No value)" bucket; the visible label is translated (metricsCharts.noValue). */
export const NO_VALUE_ID = "__no_value__";
/** Id of the bucket that merges the series beyond the cap (metricsCharts.others). */
export const OTHERS_ID = "__others__";
export const SERIES_KEY_SEPARATOR = " - ";
export const FIELD_KEY_SEPARATOR = "|";

// Same order as PROJECT_PRIORITIES_VALUES (global/constants), kept here so this pure module does
// not load the i18n runtime that constants.js initialises.
const PRIORITY_ORDER = ["MANDATORY", "P1", "P2"];
const PROJECT_STATUS_ORDER = [
  "PROSPECT", "BACKLOG", "CAPACITY", "OPEN", "EXECUTION", "IN_PROGRESS", "ON_HOLD", "CLOSED", "ARCHIVED",
];
const SCENARIO_STATUS_ORDER = ["PERT", "GANTT", "METRICS", "APPROVAL", "PENDING", "APPROVED", "REJECTED"];
const RESOURCE_TYPE_ORDER = ["INTERNAL", "EXTERNAL"];
const DATA_TYPE_ORDER = ["DEMAND", "ACTUALS", "ALLOCATION", "REMAINING", "BUDGETED"];

/**
 * Natural rank of a priority: MANDATORY, P1, P2, P3… (SPEC-0030). Values past the known list keep
 * their numeric order (P10 after P9).
 */
const priorityRank = (value) => {
  const v = String(value).trim().toUpperCase();
  const known = PRIORITY_ORDER.indexOf(v);
  if (known !== -1) return known;
  const m = /^P(\d+)$/.exec(v);
  return m ? Number(m[1]) : null;
};

const listRank = (order) => (value) => {
  const i = order.indexOf(String(value).trim().toUpperCase());
  return i === -1 ? null : i;
};

export const STANDARD_FIELDS = [
  { key: "project.name", entity: ENTITY_PROJECT, rowField: "projectName", labelKey: "metricsCharts.pivot.projectName" },
  { key: "project.type", entity: ENTITY_PROJECT, rowField: "projectType", labelKey: "metricsCharts.pivot.projectType" },
  { key: "project.status", entity: ENTITY_PROJECT, rowField: "projectStatus", labelKey: "metricsCharts.pivot.projectStatus", naturalRank: listRank(PROJECT_STATUS_ORDER) },
  { key: "project.priority", entity: ENTITY_PROJECT, rowField: "projectPriority", labelKey: "metricsCharts.fields.priority", naturalRank: priorityRank },
  { key: "project.program", entity: ENTITY_PROJECT, rowField: "programName", labelKey: "term.program" },
  { key: "scenario.name", entity: ENTITY_SCENARIO, rowField: "scenarioName", labelKey: "metricsCharts.pivot.scenarioName" },
  { key: "scenario.status", entity: ENTITY_SCENARIO, rowField: "scenarioStatus", labelKey: "metricsCharts.fields.scenarioStatus", naturalRank: listRank(SCENARIO_STATUS_ORDER) },
  { key: "resource.name", entity: ENTITY_RESOURCE, rowField: "resourceName", labelKey: "metricsCharts.pivot.resourceName" },
  { key: "resource.code", entity: ENTITY_RESOURCE, rowField: "resourceCode", labelKey: "metricsCharts.pivot.resourceCode" },
  { key: "resource.group", entity: ENTITY_RESOURCE, rowField: "resourceGroup", labelKey: "metricsCharts.pivot.resourceGroup" },
  { key: "resource.type", entity: ENTITY_RESOURCE, rowField: "resourceType", labelKey: "metricsCharts.pivot.resourceType", naturalRank: listRank(RESOURCE_TYPE_ORDER) },
  { key: "resource.skill", entity: ENTITY_RESOURCE, rowField: "resourceSkill", labelKey: "metricsCharts.fields.skill" },
  { key: "resource.site", entity: ENTITY_RESOURCE, rowField: "resourceSite", labelKey: "metricsCharts.fields.site" },
  // Activity-level fields (SPEC-0060 §4.2, SPEC-0057 §2.1): offered when the rows carry them.
  { key: "activity.name", entity: ENTITY_ACTIVITY, rowField: "taskName", labelKey: "metricsCharts.fields.activity" },
  { key: "activity.scope", entity: ENTITY_ACTIVITY, rowField: "scopeName", labelKey: "metricsCharts.fields.scope" },
  { key: "activity.tags", entity: ENTITY_ACTIVITY, rowField: "tags", labelKey: "metricsCharts.fields.tags", multi: true },
  { key: "data.type", entity: ENTITY_DATA, rowField: "type", labelKey: "metricsCharts.pivot.dataType", naturalRank: listRank(DATA_TYPE_ORDER) },
];

const STANDARD_BY_KEY = Object.fromEntries(STANDARD_FIELDS.map((f) => [f.key, f]));
const STANDARD_BY_ROW_FIELD = Object.fromEntries(STANDARD_FIELDS.map((f) => [f.rowField, f]));

// Fields with one value on a one-project page (SPEC-0060 §4.2): a Group by a constant is noise.
const CONSTANT_ON_PROJECT = new Set(["project.name", "project.type", "project.status", "project.priority", "project.program"]);

/** Maps a saved Group by value of the pre-SPEC-0053 charts (a row field name) to its field key. */
export const migrateFieldKey = (value) => {
  if (value == null || value === "") return null;
  if (STANDARD_BY_KEY[value] || /^(project|scenario|resource|activity)\.custom\./.test(value)) return value;
  return STANDARD_BY_ROW_FIELD[value]?.key || null;
};

/** A saved Group by (one key, or a list of keys) as a list of at most `max` distinct field keys. */
export const migrateGroupBy = (saved, max = 3) => {
  const list = Array.isArray(saved) ? saved : saved != null ? [saved] : [];
  const keys = [];
  list.forEach((v) => {
    const k = migrateFieldKey(v);
    if (k && !keys.includes(k)) keys.push(k);
  });
  return keys.slice(0, max);
};

const customKey = (entityPrefix, id) => `${entityPrefix}.custom.${id}`;

/**
 * Resource custom attributes travel on each usage row as `resourceAttributes`, either a map
 * `{ [attributeId]: value }` or a list `[{ attributeId|id, name, value }]`. Values may be a string,
 * a list of strings, or `{ value|label|option }`.
 */
const readResourceAttributes = (row) => {
  const raw = row?.resourceAttributes;
  if (!raw) return null;
  if (Array.isArray(raw)) {
    const out = {};
    raw.forEach((a) => {
      const id = a?.attributeId ?? a?.id;
      if (id != null) out[id] = { name: a.name ?? a.attributeName, value: a.value ?? a.values };
    });
    return out;
  }
  const out = {};
  Object.entries(raw).forEach(([id, value]) => {
    out[id] = value && typeof value === "object" && !Array.isArray(value) && "value" in value && "name" in value
      ? value
      : { value };
  });
  return out;
};

const scalarTokens = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value.flatMap(scalarTokens);
  if (typeof value === "object") {
    const v = value.value ?? value.label ?? value.option ?? value.text_value;
    return v == null ? valueTokens(value) : scalarTokens(v);
  }
  return [String(value)];
};

const readCustomColumns = (row) => {
  const raw = row?.customColumnValues ?? row?.customColumns;
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
};

/**
 * Builds the catalogue for the rows at hand. A standard field no row carries (an older backend, or
 * a field the tenant does not use) is left out, so Group by only offers what can group something.
 * Activity custom columns (`customColumns`, [{ id, name }]) are offered when some row carries values.
 * @param {string} [scope] ENTITY_PROJECT on a one-project page
 * @param {boolean} [forGroupBy] a Group by picker: on a one-project page the project's constant
 *   fields (one value, nothing to stack) are left out; the filter bar keeps them (SPEC-0060 §4.2).
 */
export const buildFieldCatalogue = ({ rows, projectAttributes, scenarioAttributes, customColumns, scope, forGroupBy = false }) => {
  const list = rows || [];
  const carried = new Set();
  const resourceAttrs = new Map();
  let hasCustomColumns = false;
  for (const row of list) {
    if (!row) continue;
    for (const f of STANDARD_FIELDS) {
      if (!carried.has(f.key) && Object.prototype.hasOwnProperty.call(row, f.rowField)) carried.add(f.key);
    }
    const ra = readResourceAttributes(row);
    if (ra) {
      Object.entries(ra).forEach(([id, a]) => {
        if (!resourceAttrs.has(id) || (!resourceAttrs.get(id) && a.name)) resourceAttrs.set(id, a.name || null);
      });
    }
    if (!hasCustomColumns && readCustomColumns(row)) hasCustomColumns = true;
  }
  const dropConstant = forGroupBy && scope === ENTITY_PROJECT;
  const fields = STANDARD_FIELDS.filter((f) => carried.has(f.key) && !(dropConstant && CONSTANT_ON_PROJECT.has(f.key))).map((f) => ({ ...f, custom: false }));
  (projectAttributes || []).forEach((a) =>
    fields.push({ key: customKey("project", a.id), entity: ENTITY_PROJECT, attributeId: a.id, label: a.attribute_name, custom: true })
  );
  (scenarioAttributes || []).forEach((a) =>
    fields.push({ key: customKey("scenario", a.id), entity: ENTITY_SCENARIO, attributeId: a.id, label: a.attribute_name, custom: true })
  );
  resourceAttrs.forEach((name, id) =>
    fields.push({ key: customKey("resource", id), entity: ENTITY_RESOURCE, attributeId: id, label: name || `#${id}`, custom: true })
  );
  if (hasCustomColumns) {
    (customColumns || []).forEach((c) =>
      fields.push({ key: customKey("activity", c.id), entity: ENTITY_ACTIVITY, attributeId: c.id, label: c.name || `#${c.id}`, custom: true })
    );
  }
  return fields;
};

/** Visible label of a field (translated for standard fields). */
export const fieldLabel = (field, t) => (field ? field.label ?? t(field.labelKey) : "");

const attributeValueOf = (valuesMap, entityId, attributeId) => {
  if (entityId == null || !valuesMap) return null;
  const values = valuesMap[entityId] || valuesMap[String(entityId)] || [];
  return values.find((v) => v && String(v.attribute_id) === String(attributeId)) || null;
};

const joinTokens = (tokens) =>
  tokens.length === 0 ? NO_VALUE_ID : Array.from(new Set(tokens)).sort((a, b) => a.localeCompare(b)).join(", ");

const fieldTokens = (row, fieldKey, ctx) => {
  const std = STANDARD_BY_KEY[fieldKey];
  if (std) {
    const v = row?.[std.rowField];
    if (std.multi) return scalarTokens(v);
    return v == null || v === "" ? [] : [String(v)];
  }
  const m = /^(project|scenario|resource|activity)\.custom\.(.+)$/.exec(fieldKey || "");
  if (!m || !row) return [];
  const [, entity, attributeId] = m;
  if (entity === "resource") {
    const a = readResourceAttributes(row)?.[attributeId];
    return a ? scalarTokens(a.value) : [];
  }
  if (entity === "activity") return scalarTokens(readCustomColumns(row)?.[attributeId]);
  const scenarioId = row.scenarioId;
  const value =
    entity === "project"
      ? attributeValueOf(ctx.projectValuesMap, row.projectId ?? (ctx.scenarioProjectMap || {})[scenarioId], attributeId)
      : attributeValueOf(ctx.scenarioValuesMap, scenarioId, attributeId);
  return valueTokens(value);
};

/**
 * The value id of one field on one usage row: the raw value for a standard field, the selected
 * option(s) for a custom attribute, NO_VALUE_ID when empty.
 * @param {object} ctx { projectValuesMap, scenarioValuesMap, scenarioProjectMap }
 */
export const resolveFieldValue = (row, fieldKey, ctx = {}) => joinTokens(fieldTokens(row, fieldKey, ctx));

/**
 * The value ids of one field on one usage row, one per selected option (a multi-select attribute
 * carries several), [NO_VALUE_ID] when empty: a filter on one option matches a row that carries it
 * among others.
 * @param {object} ctx { projectValuesMap, scenarioValuesMap, scenarioProjectMap }
 */
export const resolveFieldTokens = (row, fieldKey, ctx = {}) => {
  const tokens = fieldTokens(row, fieldKey, ctx);
  return tokens.length === 0 ? [NO_VALUE_ID] : Array.from(new Set(tokens));
};

/** Builds a `(row, fieldKey) => valueId` resolver bound to the attribute maps. */
export const makeFieldResolver = (ctx) => (row, fieldKey) => resolveFieldValue(row, fieldKey, ctx);

/** Combined value id of several fields: values joined in field order. */
export const comboValueId = (row, fieldKeys, resolve) =>
  fieldKeys.map((k) => resolve(row, k)).join(SERIES_KEY_SEPARATOR);

export const comboFieldKey = (fieldKeys) => fieldKeys.join(FIELD_KEY_SEPARATOR);

/** Visible label of a (combined) value id. */
const VALUE_LABEL_KEYS = { "project.priority": "metricsCharts.priorities" };

/** Visible label of a (combined) value id; `fieldKeys` lets enum values read as words (MANDATORY → Mandatory). */
export const valueLabel = (valueId, t, fieldKeys = []) => {
  if (valueId === OTHERS_ID) return t("metricsCharts.others");
  return String(valueId)
    .split(SERIES_KEY_SEPARATOR)
    .map((part, i) => {
      if (part === NO_VALUE_ID) return t("metricsCharts.noValue");
      const ns = VALUE_LABEL_KEYS[fieldKeys[i]];
      return ns ? t(`${ns}.${part}`, { defaultValue: part }) : part;
    })
    .join(SERIES_KEY_SEPARATOR);
};

const compareSingle = (field, a, b) => {
  if (a === b) return 0;
  if (a === NO_VALUE_ID) return 1;
  if (b === NO_VALUE_ID) return -1;
  const ra = field?.naturalRank ? field.naturalRank(a) : null;
  const rb = field?.naturalRank ? field.naturalRank(b) : null;
  if (ra != null && rb != null && ra !== rb) return ra - rb;
  if (ra != null && rb == null) return -1;
  if (rb != null && ra == null) return 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
};

/**
 * Default order of (combined) value ids: lexicographic on each field's natural order (then
 * alphabetical), "(No value)" last at every level.
 */
export const defaultValueOrder = (valueIds, fieldKeys) => {
  const fields = fieldKeys.map((k) => STANDARD_BY_KEY[k] || null);
  const split = (v) => String(v).split(SERIES_KEY_SEPARATOR);
  return Array.from(new Set(valueIds)).sort((a, b) => {
    const pa = split(a);
    const pb = split(b);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const c = compareSingle(fields[i], pa[i] ?? "", pb[i] ?? "");
      if (c !== 0) return c;
    }
    return 0;
  });
};

/** Distinct sorted values of one field across rows, for a filter list. */
export const distinctFieldValues = (rows, fieldKey, resolve) => {
  const seen = new Set();
  (rows || []).forEach((row) => row && seen.add(resolve(row, fieldKey)));
  return defaultValueOrder(Array.from(seen), [fieldKey]);
};
